import type { MergedRewardData, RewardOverviewData } from '@/schemas/v2/reward'

type AcquisitionStatus = NonNullable<MergedRewardData['acquisitionStatus']>

/**
 * グループメンバーの獲得状況を代表値に集約する。
 * 優先順: ACHIEVABLE > ACHIEVED > UNOBTAINED > (EXCHANGED/USED/UNAPPLIED/APPLIED/LOST) > ENDED/SOLD_OUT
 * 全メンバーが ENDED/SOLD_OUT の場合のみ終了系ステータスを返す。
 * （認証済みの場合のみ設定される。未認証時は全 null のため undefined を返す）
 */
function resolveGroupAcquisitionStatus(members: MergedRewardData[]): AcquisitionStatus | undefined {
  const statuses = members.map((m) => m.acquisitionStatus).filter((s): s is AcquisitionStatus => s != null)
  if (statuses.length === 0) return undefined
  if (statuses.includes('ACHIEVABLE')) return 'ACHIEVABLE'
  if (statuses.includes('ACHIEVED')) return 'ACHIEVED'
  if (statuses.includes('UNOBTAINED')) return 'UNOBTAINED'
  // ENDED/SOLD_OUT 以外のステータス（EXCHANGED/USED/UNAPPLIED/APPLIED/LOST）が 1 件でも含まれれば
  // 終了系にしない（全メンバーが ENDED/SOLD_OUT の場合のみ終了系を返す）
  const nonEndedStatuses = statuses.filter((s) => s !== 'ENDED' && s !== 'SOLD_OUT')
  if (nonEndedStatuses.length > 0) return nonEndedStatuses[0]
  return statuses[0]
}

/**
 * MergedRewardData[] を RewardOverviewData[] に変換する。
 * - groupType === 'NONE': そのまま個別エントリ
 * - groupType !== 'NONE': 同じ groupId を持つものを1エントリにまとめる
 *   - name: グループ名 (groupName)、null の場合は代表特典名にフォールバック
 *   - label: "N件の特典"
 *   - id / image: グループ先頭特典（APIレスポンス順に依存）
 *   - acquisitionStatus: resolveGroupAcquisitionStatus の結果
 */
export function buildDisplayRewards(rewards: MergedRewardData[]): RewardOverviewData[] {
  const groupMembersMap = new Map<number, MergedRewardData[]>()
  for (const r of rewards) {
    if (r.groupType !== 'NONE' && r.groupId !== null) {
      const members = groupMembersMap.get(r.groupId) ?? []
      members.push(r)
      groupMembersMap.set(r.groupId, members)
    }
  }

  const result: RewardOverviewData[] = []
  const seenGroupIds = new Set<number>()

  for (const r of rewards) {
    if (r.groupType === 'NONE' || r.groupId === null) {
      result.push({
        id: r.id,
        name: r.name,
        image: r.image,
        label: r.label,
        acquisitionStatus: r.acquisitionStatus ?? undefined,
        conditions: r.conditions,
      })
    } else {
      if (!seenGroupIds.has(r.groupId)) {
        seenGroupIds.add(r.groupId)
        const groupMembers = groupMembersMap.get(r.groupId) ?? [r]
        result.push({
          id: r.id,
          name: r.groupName ?? r.name,
          image: r.image,
          label: `${groupMembers.length}件の特典`,
          acquisitionStatus: resolveGroupAcquisitionStatus(groupMembers),
          conditions: groupMembers[0]?.conditions ?? [],
        })
      }
    }
  }
  return result
}
